// Popup de una tienda en «Turbo · En vivo». Leaflet lo dibuja como HTML suelto
// y se rearma con cada sondeo (estados, choferes, distancias): lo que el
// coordinador eligió vive en el DOM y hay que cuidarlo.
//
// Dos vistas, las mismas para todos (las elige un Administrador Total en
// Turbo → Configuración):
//   · acordeon: una línea por Turbo; al tocarla se abre con el motivo, el
//     chofer y «Ofrecer», y lo demás queda detrás de «Más acciones».
//   · dos_pasos: la lista de los Turbos y, al tocar uno, su detalle con todas
//     las acciones y «‹ volver».
// Todo va en el HTML y se abre o se cierra con `hidden` (alternarPopup): lo
// abierto se lee del DOM antes de rearmar y se repone después (refrescarPopup).

import { driverName } from "./driverName";
import { TURBO_ESTADO_COLOR, TURBO_ESTADO_LABEL, TurboHoy } from "./mapLayers";

export type VistaPopup = "acordeon" | "dos_pasos";
export type VistaLista = "linea" | "tienda" | "tabla";
export type VistaVivo = { popup: VistaPopup; lista: VistaLista };
// La vista que manda /live. Sin el dato (servidor viejo) o con un valor que
// no se conoce, la inicial.
export const vistaDe = (v: Partial<VistaVivo> | null | undefined): VistaVivo => ({
  popup: v?.popup === "dos_pasos" ? "dos_pasos" : "acordeon",
  lista: v?.lista === "tienda" || v?.lista === "tabla" ? v.lista : "linea",
});

// turbo_enabled: la marca «tiene envíos Turbo» de la ficha. Llega en false
// solo para una tienda sin marcar que sale porque hoy le entró un Turbo.
export type Store = { id: number; name: string; address: string | null; lat: number; lng: number; turbo_enabled?: boolean; estado: "SIN_TURBO" | "CON_TURBO" | "ESCALADO"; turbos: { total: number; abiertos: number; escalados: number; ofreciendo: number; entregados: number; programados?: number } };
export type Driver = {
  driver_id: number; username: string; first_name: string | null; last_name: string | null; phone: string | null; vehiculo: string | null; tipo: "moto" | "auto";
  lat: number; lng: number; recorded_at: string; source: string; turbo_enabled: boolean; turbo_active: boolean; turbo_route_id: number | null; turbos_abiertos: number;
  estado: "ESPERANDO" | "A_RETIRAR" | "ENTREGANDO" | "APAGADO";
  // Sus grupos en curso y la peor holgura de sus entregas (null: sin plan).
  plan?: { grupos: number; holgura_min: number | null; estimado: boolean; calculado_at: string | null } | null;
};
// Lo del último sondeo que usa el popup.
export type VivoPopup = { turbos: TurboHoy[]; drivers: Driver[] };

export const SIN_SENAL_MIN = 10;
export const COLOR = { gris: "#8b949e", verde: "#3fb950", rojo: "#f85149", azul: "#4f8cff", naranja: "#f0883e", violeta: "#a371f7", amarillo: "#d29922" };
export const DRIVER_ESTADO: Record<Driver["estado"], { label: string; color: string }> = {
  ESPERANDO: { label: "esperando un Turbo", color: COLOR.azul },
  A_RETIRAR: { label: "yendo a retirar", color: COLOR.naranja },
  ENTREGANDO: { label: "entregando", color: COLOR.verde },
  APAGADO: { label: "con el Turbo apagado", color: COLOR.gris },
};
export const STORE_ESTADO: Record<Store["estado"], { label: string; color: string }> = {
  SIN_TURBO: { label: "sin Turbo hoy", color: COLOR.gris },
  CON_TURBO: { label: "con Turbo en curso", color: COLOR.verde },
  ESCALADO: { label: "Turbo sin chofer: pasó a administración", color: COLOR.rojo },
};

// La hora en Argentina, no la de la PC (una PC en otra zona mostraba otra hora).
export const fmtTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }) : "—");
export const fmtDia = (v: string) => new Date(v).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", timeZone: "America/Argentina/Buenos_Aires" });
// Una hora de un Turbo. Si se arrastra de un día anterior, con el día adelante
// («30/10 23:40»): sin eso, uno de ayer se leía como de hoy.
export const fmtHoraTurbo = (t: Pick<TurboHoy, "de_dias_anteriores">, v: string | null | undefined) => (v && t.de_dias_anteriores ? `${fmtDia(v)} ${fmtTime(v)}` : fmtTime(v));
// El vencimiento de un Turbo: en un programado, con el día («04/11 13:00»),
// porque vence otro día y solo la hora se leía como de hoy.
export const fmtVence = (t: Pick<TurboHoy, "de_dias_anteriores" | "estado">, v: string | null | undefined) =>
  (v && t.estado === "PROGRAMADO" ? `${fmtDia(v)} ${fmtTime(v)}` : fmtHoraTurbo(t, v));
// «llega ~11:40 · sobran 25 min» / «llega ~12:10 · 5 min tarde»: la hora
// estimada de la entrega según el plan del grupo. null sin plan.
export function fmtPlan(p: { eta: string | null; holgura_min: number | null; estimado: boolean } | null | undefined): string | null {
  if (!p?.eta) return null;
  const h = p.holgura_min;
  const holgura = h == null ? "" : h < 0 ? ` · ${-h} min tarde` : ` · sobran ${h} min`;
  return `llega ~${fmtTime(p.eta)}${holgura}${p.estimado ? " (sin calles)" : ""}`;
}
// «35 min», «1 h 5 min», de segundos (el signo no cuenta).
export const fmtMin = (s: number) => { const m = Math.round(Math.abs(s) / 60); return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`; };
// Ya pasó su vencimiento (un programado vence otro día: no cuenta).
export const vencido = (t: Pick<TurboHoy, "estado" | "terminal" | "seconds_to_deadline">) =>
  !t.terminal && t.estado !== "PROGRAMADO" && t.seconds_to_deadline != null && t.seconds_to_deadline < 0;
// La línea corta de un Turbo (la fila del popup): el estado y cuándo llega
// según el plan del grupo o, sin plan, cuándo vence. Uno vencido dice solo
// «vencido» (con el día y la hora, de días anteriores, tapaba el nombre).
export function resumenTurbo(t: Pick<TurboHoy, "estado" | "terminal" | "plan" | "vence_at" | "seconds_to_deadline" | "de_dias_anteriores">): string {
  const st = TURBO_ESTADO_LABEL[t.estado];
  const p = t.plan;
  if (p?.eta) return `${st} · llega ~${fmtTime(p.eta)}${p.holgura_min != null && p.holgura_min < 0 ? ` · ${-p.holgura_min} min tarde` : ""}`;
  if (vencido(t)) return `${st} · vencido`;
  return t.vence_at ? `${st} · vence ${fmtVence(t, t.vence_at)}` : st;
}
export const minutosDesde = (iso: string, now: number) => Math.max(0, (now - new Date(iso).getTime()) / 60000);
export const nombre = (d: { username: string; first_name: string | null; last_name: string | null }) => driverName(d);
export const esc = (s: unknown) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
export const distKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 6371, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};
export const SIN_MARCAR_AVISO = "No está marcada con envíos Turbo: aparece porque hoy le entró uno. Se marca en Tiendas → Editar.";
export const sinMarcar = (s: Pick<Store, "turbo_enabled"> | null | undefined) => s?.turbo_enabled === false;
// El domicilio del comprador en una línea («Av. Rivadavia 5000 · CP 1424 ·
// Caballito»), para el popup y el título de la lista (pedido de Mariano). El
// CP y la localidad no se repiten si ya vienen en la dirección. Sin dirección,
// vacío.
export function domicilioDe(t: Pick<TurboHoy, "direccion" | "localidad" | "codigo_postal">): string {
  const dir = String(t.direccion ?? "").trim();
  if (!dir) return "";
  const cp = String(t.codigo_postal ?? "").trim();
  const loc = String(t.localidad ?? "").trim();
  return [dir, cp && !dir.includes(cp) ? `CP ${cp}` : "", loc && !dir.toLowerCase().includes(loc.toLowerCase()) ? loc : ""].filter(Boolean).join(" · ");
}
// «Copiar» del domicilio: al portapapeles. Si el navegador no deja (sin
// permiso o sin https), queda seleccionado para copiarlo con Ctrl+C.
export function copiarDomicilio(b: HTMLElement): Promise<void> {
  const texto = b.dataset.copiar ?? "";
  const listo = () => { b.textContent = "Copiada"; setTimeout(() => { b.textContent = "Copiar"; }, 1500); };
  const seleccionar = () => {
    const span = b.previousElementSibling;
    const sel = window.getSelection();
    if (span && sel) { const r = document.createRange(); r.selectNodeContents(span); sel.removeAllRanges(); sel.addRange(r); }
    b.textContent = "Ctrl+C para copiar";
  };
  try { return navigator.clipboard.writeText(texto).then(listo, seleccionar); } catch { seleccionar(); return Promise.resolve(); }
}
export const despachoVivoDe = (t: TurboHoy) => !!t.despacho && t.despacho.status !== "CANCELLED" && ["PLANNED", "ASSIGNED"].includes(t.despacho.route_status ?? "");

// El contenido del popup de una tienda: sus Turbos abiertos y, para los que
// están SIN_CHOFER o PROGRAMADO, ofrecérselo a un chofer, volver a las reglas
// (en un programado, ofrecerlo ahora) o asignarlo sin preguntar. Solo esos:
// un Turbo en camino sin recorrido o no entregado no tiene a quién dárselo
// (fallaba siempre) y la asignación a mano da por visto «sin chofer» (ver
// pedidoAsignar).
// `tiendaSel`: la tienda por la que está filtrada la pantalla (el botón del
// final filtra por esta tienda o vuelve a todas). `vista`: acordeón o dos
// pasos (ver arriba). `abrir`: el Turbo que se muestra abierto (se tocó en la
// lista); sin él, abierto solo si es el único.
export function htmlTienda(s: Store, live: VivoPopup, ahora: number, tiendaSel: number | null = null,
  { vista = "acordeon", abrir = null }: { vista?: VistaPopup; abrir?: number | null } = {}): string {
  const turbos = live.turbos.filter((t) => t.store_id === s.id);
  const abiertos = turbos.filter((t) => !t.terminal);
  // Los cerrados, por separado: un cancelado no es un entregado.
  const entregados = turbos.filter((t) => t.estado === "ENTREGADO").length;
  const cancelados = turbos.filter((t) => t.estado === "CANCELADO").length;
  // Primero los que están esperando (libres y con el Turbo prendido), por
  // cercanía; después los demás, también por cercanía.
  const candidatos = live.drivers
    .map((d) => ({ d, km: distKm(d, s), libre: d.estado === "ESPERANDO" && minutosDesde(d.recorded_at, ahora) <= SIN_SENAL_MIN ? 0 : 1 }))
    .sort((a, b) => a.libre - b.libre || a.km - b.km);
  const opciones = candidatos.map(({ d, km }) => `<option value="${d.driver_id}" data-nombre="${esc(nombre(d))}">${esc(nombre(d))} · ${d.tipo} · ${km.toFixed(1)} km · ${esc(DRIVER_ESTADO[d.estado].label)}${minutosDesde(d.recorded_at, ahora) > SIN_SENAL_MIN ? " · sin señal" : ""}</option>`).join("");
  // Las acciones son para los SIN_CHOFER y los PROGRAMADO.
  const accionable = (t: TurboHoy) => t.estado === "SIN_CHOFER" || t.estado === "PROGRAMADO";
  const destinatario = (t: TurboHoy) => esc(t.nombre_destinatario ?? t.tracking ?? t.delivery_id);
  // A dónde va, debajo del nombre, con «Copiar» (para pasárselo a un chofer).
  const domicilio = (t: TurboHoy) => {
    const d = domicilioDe(t);
    return d ? `<div class="m tv-dom"><span>${esc(d)}</span><button type="button" class="tv-copiar" data-copiar="${esc(d)}" aria-label="Copiar la dirección">Copiar</button></div>` : "";
  };
  const punto = (t: TurboHoy) => `<span class="tv-dot" style="background:${TURBO_ESTADO_COLOR[t.estado]}"></span>`;
  // Quién lo lleva o por qué no tiene chofer, y el plan del grupo.
  const quien = (t: TurboHoy) => {
    const km = t.distancia_km != null ? `${Number(t.distancia_km).toFixed(1)} km` : "";
    // El motivo (en rojo) es para los que están SIN_CHOFER. Uno en camino o no
    // entregado sin chofer de Lynx (lo llevó la tienda por fuera) no tiene un
    // «motivo»: se dice qué pasa, sin alarma.
    const linea = t.chofer
      ? `<div class="m">${esc(nombre(t.chofer))}${t.detalle ? ` · ${esc(t.detalle)}` : ""}${km ? ` · ${km}` : ""}</div>`
      : t.estado === "PROGRAMADO"
        ? `<div class="m" style="color:${TURBO_ESTADO_COLOR.PROGRAMADO}">${esc(t.detalle ?? "programado")}</div>${km ? `<div class="m">comprador a ${km} de la tienda</div>` : ""}`
      : t.estado === "SIN_CHOFER"
        ? `<div class="m" style="color:${TURBO_ESTADO_COLOR.SIN_CHOFER}">${motivoSinChofer(t)}</div>${km ? `<div class="m">comprador a ${km} de la tienda</div>` : ""}`
        : `<div class="m">${t.detalle ? `${esc(t.detalle)} · ` : ""}sin chofer de Lynx${km ? ` · comprador a ${km} de la tienda` : ""}</div>`;
    const plan = fmtPlan(t.plan);
    return plan ? `${linea}<div class="m">${esc(plan)}</div>` : linea;
  };
  const selector = (t: TurboHoy) => `<select id="tv-sel-${t.delivery_id}" aria-label="Chofer">${opciones || "<option value=''>No hay choferes reportando</option>"}</select>`;
  const ofrecer = (t: TurboHoy) => `<button type="button" data-ofrecer-a="${t.delivery_id}">${candidatos[0] ? `Ofrecer a ${esc(nombre(candidatos[0].d))}` : "Ofrecer a este chofer"}</button>`;
  // Un programado admite lo mismo, salvo que volver a las reglas es
  // ofrecerlo ahora, antes de su día.
  const otras = (t: TurboHoy) => `<button type="button" class="sec" data-elegir="${t.delivery_id}">Elegir tocando el mapa</button>`
    + `<button type="button" class="sec" data-reofrecer="${t.delivery_id}">${t.estado === "PROGRAMADO" ? "Ofrecer ahora" : "Reofrecer a todos según las reglas"}</button>`
    + `<button type="button" class="sec" data-asignar="${t.delivery_id}">Asignar sin preguntar</button>`;
  const linea = (t: TurboHoy, attr: string) => `<button type="button" class="tv-fila" ${attr}>${punto(t)}<span class="tv-n">${destinatario(t)}</span><span class="tv-r">${esc(resumenTurbo(t))}</span><span class="tv-chev" aria-hidden="true">›</span></button>`;
  const ninguno = `<div class="t m">Ningún Turbo abierto ahora.</div>`;
  const pedido = abiertos.some((t) => t.delivery_id === abrir) ? abrir : abiertos.length === 1 ? abiertos[0].delivery_id : null;

  let cuerpo: string;
  if (vista === "dos_pasos") {
    // La lista y un detalle por Turbo; se ve uno de los dos. El único Turbo
    // va directo a su detalle (sin «volver»: no hay otra cosa que ver).
    const filas = abiertos.map((t) => linea(t, `data-detalle="${t.delivery_id}"`)).join("");
    const detalles = abiertos.map((t) => {
      const st = TURBO_ESTADO_LABEL[t.estado];
      const vence = t.vence_at ? ` · vence ${fmtVence(t, t.vence_at)}` : "";
      const volver = abiertos.length > 1 ? `<button type="button" class="tv-volver" data-volver="">‹ Los ${abiertos.length} Turbos de la tienda</button>` : "";
      const acciones = accionable(t) ? `${selector(t)}${ofrecer(t)}${otras(t)}` : "";
      return `<div class="tv-lista" data-detalle-de="${t.delivery_id}" data-scroll="d${t.delivery_id}"${t.delivery_id === pedido ? "" : " hidden"}>${volver}`
        + `<div class="t"><b>${destinatario(t)}</b> <span style="color:${TURBO_ESTADO_COLOR[t.estado]}">${esc(st)}</span><span class="m">${vence}</span>${domicilio(t)}${quien(t)}${acciones}</div></div>`;
    }).join("");
    cuerpo = `<div class="tv-lista" data-paso-lista="" data-scroll="lista"${pedido != null ? " hidden" : ""}>${filas || ninguno}</div>${detalles}`;
  } else {
    // Una línea por Turbo; se abre uno a la vez. Abierto: quién o el motivo,
    // el chofer y «Ofrecer»; lo demás, detrás de «Más acciones».
    const filas = abiertos.map((t) => {
      const abierta = t.delivery_id === pedido;
      // La línea dice solo «vencido»: abierto, cuándo venció.
      const vencio = vencido(t) && t.vence_at ? `<div class="m">venció ${esc(fmtVence(t, t.vence_at))}</div>` : "";
      const acciones = !accionable(t) ? "" : `${selector(t)}<div class="tv-acc">${ofrecer(t)}<button type="button" class="tv-mas" data-mas="${t.delivery_id}" aria-expanded="false">Más acciones</button></div>`
        + `<div class="tv-mas-de" data-mas-de="${t.delivery_id}" hidden>${otras(t)}</div>`;
      return `<div class="tv-t">${linea(t, `data-fila="${t.delivery_id}" aria-expanded="${abierta}"`)}`
        + `<div class="tv-cuerpo" data-cuerpo="${t.delivery_id}"${abierta ? "" : " hidden"}>${domicilio(t)}${vencio}${quien(t)}${acciones}</div></div>`;
    }).join("");
    cuerpo = `<div class="tv-lista" data-scroll="lista">${filas || ninguno}</div>`;
  }
  // La marca «Tiene envíos Turbo» se pone y se saca también desde acá (como en
  // Tiendas → Editar). Sin el dato (servidor viejo), no se ofrece.
  const aviso = sinMarcar(s)
    ? `<div class="m" style="color:${COLOR.amarillo};opacity:1">⚠ ${esc(SIN_MARCAR_AVISO)}</div><button type="button" data-marcar-turbo="${s.id}">Marcar con envíos Turbo</button>`
    : "";
  const desmarcar = s.turbo_enabled === true ? `<button type="button" class="sec" data-desmarcar-turbo="${s.id}">Quitar la marca de Turbo</button>` : "";
  // Solo en una tienda con Turbos hoy: ver nada más sus puntos en el mapa.
  const filtro = !turbos.length ? "" : tiendaSel === s.id
    ? `<button type="button" class="sec" data-solo-tienda="">Ver todas las tiendas</button>`
    : `<button type="button" class="sec" data-solo-tienda="${s.id}">Ver solo esta tienda</button>`;
  return `<div class="tv-pop" data-vista="${vista}"><b>${esc(s.name)}</b>${aviso}<div class="m">${esc(s.address ?? "")}${s.address ? " · " : ""}${esc(STORE_ESTADO[s.estado].label)}${entregados ? ` · ${entregados} entregado${entregados === 1 ? "" : "s"} hoy` : ""}${cancelados ? ` · ${cancelados} cancelado${cancelados === 1 ? "" : "s"}` : ""}</div>${cuerpo}<div class="tv-pie">${filtro}${desmarcar}</div></div>`;
}

/* ===== Abrir y cerrar partes del popup (no le piden nada al servidor) ===== */

const uno = (root: ParentNode, sel: string) => root.querySelector(sel) as HTMLElement | null;

// Acordeón: abre ese Turbo y cierra los demás (null: todos cerrados).
export function abrirFila(root: ParentNode, id: string | null) {
  root.querySelectorAll<HTMLElement>("[data-fila]").forEach((b) => {
    const abierta = b.dataset.fila === id;
    b.setAttribute("aria-expanded", String(abierta));
    const cuerpo = uno(root, `[data-cuerpo="${b.dataset.fila}"]`);
    if (cuerpo) cuerpo.hidden = !abierta;
  });
}

// Acordeón: «Más acciones» de un Turbo.
export function abrirMas(root: ParentNode, id: string, abierto: boolean) {
  const b = uno(root, `[data-mas="${id}"]`);
  const extra = uno(root, `[data-mas-de="${id}"]`);
  if (!b || !extra) return;
  b.setAttribute("aria-expanded", String(abierto));
  b.textContent = abierto ? "Menos acciones" : "Más acciones";
  extra.hidden = !abierto;
}

// Dos pasos: el detalle de ese Turbo, o la lista (null o un Turbo que ya no está).
export function verDetalle(root: ParentNode, id: string | null) {
  const hay = id != null && !!uno(root, `[data-detalle-de="${id}"]`);
  const lista = uno(root, "[data-paso-lista]");
  if (lista) lista.hidden = hay;
  root.querySelectorAll<HTMLElement>("[data-detalle-de]").forEach((d) => { d.hidden = !(hay && d.dataset.detalleDe === id); });
}

// Lo que hace un botón de los que abren o cierran; false si no es uno de esos.
export function alternarPopup(root: ParentNode, b: HTMLElement): boolean {
  const d = b.dataset;
  if (d.fila !== undefined) abrirFila(root, b.getAttribute("aria-expanded") === "true" ? null : d.fila);
  else if (d.mas !== undefined) abrirMas(root, d.mas, b.getAttribute("aria-expanded") !== "true");
  else if (d.detalle !== undefined) verDetalle(root, d.detalle);
  else if (d.volver !== undefined) verDetalle(root, null);
  else return false;
  return true;
}

// Lo abierto en el popup: el Turbo del acordeón, sus «Más acciones», el
// detalle de dos pasos y hasta dónde se bajó en cada lista.
export type EstadoPopup = { fila: string | null; mas: string[]; detalle: string | null; scroll: Record<string, number> };
export function leerEstado(root: ParentNode): EstadoPopup {
  const scroll: Record<string, number> = {};
  root.querySelectorAll<HTMLElement>("[data-scroll]").forEach((d) => { scroll[d.dataset.scroll!] = d.scrollTop; });
  return {
    fila: uno(root, '[data-fila][aria-expanded="true"]')?.dataset.fila ?? null,
    mas: Array.from(root.querySelectorAll<HTMLElement>('[data-mas][aria-expanded="true"]')).map((b) => b.dataset.mas!),
    detalle: Array.from(root.querySelectorAll<HTMLElement>("[data-detalle-de]")).find((d) => !d.hidden)?.dataset.detalleDe ?? null,
    scroll,
  };
}
// Lo repone sobre el HTML nuevo. Lo que ya no está (un Turbo que se cerró, la
// otra vista) se ignora.
export function aplicarEstado(root: ParentNode, e: EstadoPopup) {
  if (uno(root, "[data-fila]")) abrirFila(root, e.fila);
  for (const id of e.mas) abrirMas(root, id, true);
  if (uno(root, "[data-detalle-de]")) verDetalle(root, e.detalle);
  root.querySelectorAll<HTMLElement>("[data-scroll]").forEach((d) => { const v = e.scroll[d.dataset.scroll!]; if (v) d.scrollTop = v; });
}

// El Turbo que se tocó en la lista, abierto en el popup ya abierto.
export function mostrarTurbo(root: ParentNode, id: number) {
  if (uno(root, "[data-fila]")) abrirFila(root, String(id));
  if (uno(root, "[data-detalle-de]")) verDetalle(root, String(id));
}

// Cuánto correr el mapa para que el popup, que crece hacia arriba al abrir
// una parte, no quede cortado arriba (Leaflet lo encuadra solo al abrirlo).
// Negativo: el mapa baja. 0: entra.
export function desborde(popup: Pick<DOMRect, "top">, mapa: Pick<DOMRect, "top">, margen = 10): number {
  return popup.top < mapa.top + margen ? popup.top - mapa.top - margen : 0;
}

// Por qué un Turbo no tiene chofer (HTML). Con despacho, lo dice el despacho.
// Sin despacho, el servidor lo deduce con las reglas y la configuración de
// ahora, no las del alta: es un motivo probable y así se rotula (el servidor
// nuevo ya lo escribe en el texto; si no, lo dice el rótulo).
export function motivoSinChofer(t: Pick<TurboHoy, "despacho" | "detalle">): string {
  const d = t.detalle ?? "sin chofer";
  if (t.despacho) return `<b>Motivo:</b> ${esc(d)}`;
  return /probable/i.test(d) ? esc(d) : `<b>Motivo probable:</b> ${esc(d)}`;
}

// El pedido de la asignación a mano. Solo se ofrece asignar un Turbo que se ve
// SIN_CHOFER (popup y lista), así que lo que vio el coordinador es siempre
// «sin chofer»: va null y, si en el medio lo aceptó alguien, el servidor
// contesta 409 en vez de pisarlo. Antes salía del último sondeo, que ya podía
// traer al chofer que acababa de aceptar mientras el popup (con el foco
// adentro no se rearma) lo seguía mostrando sin chofer: el servidor lo daba
// por visto y lo reemplazaba.
export const pedidoAsignar = (turbo: Pick<TurboHoy, "delivery_id">, driverId: number) =>
  ({ delivery_id: turbo.delivery_id, driver_id: driverId, expected_driver_id: null as number | null });

export type AccionesPopup = {
  // Ofrecérselo al chofer del selector: lo acepta o lo rechaza en la app.
  ofrecerA: (turbo: TurboHoy, driverId: number) => void;
  elegir: (turbo: TurboHoy) => void;
  // Volver a las reglas: chofer asignado a la tienda, anillos por cercanía y
  // escalado si nadie lo toma.
  reofrecer: (turbo: TurboHoy) => void;
  // Dárselo al chofer del selector sin que lo acepte (lo confirmó por teléfono).
  asignar: (turbo: TurboHoy, driverId: number) => void;
  // Filtrar la pantalla por una tienda (null = todas).
  soloTienda?: (storeId: number | null) => void;
  // Poner o sacar la marca «Tiene envíos Turbo» de la tienda.
  marcarTurbo?: (storeId: number, enabled: boolean) => void;
  // Después de abrir o cerrar una parte: que el popup entre en el mapa.
  encuadrar?: () => void;
};

// Los botones del popup son HTML suelto: se escuchan por delegación en el
// contenedor del mapa. El Turbo sale del último sondeo (`live`); mientras
// corre una acción (`ocupado`) no se toma otra. Devuelve cómo dejar de escuchar.
export function escucharPopup(el: HTMLElement, live: () => VivoPopup | null, ocupado: () => boolean, acciones: AccionesPopup): () => void {
  const onClick = (ev: Event) => {
    // Copiar la dirección no le pide nada al servidor: va aunque corra otra acción.
    const c = (ev.target as HTMLElement).closest("[data-copiar]") as HTMLElement | null;
    if (c) { ev.preventDefault(); void copiarDomicilio(c); return; }
    // Abrir o cerrar una parte del popup solo cambia lo que se ve: va aunque
    // corra otra acción.
    const p = (ev.target as HTMLElement).closest("[data-fila],[data-mas],[data-detalle],[data-volver]") as HTMLElement | null;
    if (p) {
      ev.preventDefault();
      if (alternarPopup(p.closest(".tv-pop") ?? el, p)) acciones.encuadrar?.();
      return;
    }
    // Filtrar por tienda tampoco le pide nada al servidor.
    const f = (ev.target as HTMLElement).closest("[data-solo-tienda]") as HTMLElement | null;
    if (f) { ev.preventDefault(); acciones.soloTienda?.(f.dataset.soloTienda ? Number(f.dataset.soloTienda) : null); return; }
    // La marca de Turbo sí va al servidor: una acción a la vez.
    const m = (ev.target as HTMLElement).closest("[data-marcar-turbo],[data-desmarcar-turbo]") as HTMLElement | null;
    if (m) {
      ev.preventDefault();
      if (ocupado()) return;
      const marcar = m.dataset.marcarTurbo !== undefined;
      acciones.marcarTurbo?.(Number(marcar ? m.dataset.marcarTurbo : m.dataset.desmarcarTurbo), marcar);
      return;
    }
    const datos = live(); if (!datos) return;
    const b = (ev.target as HTMLElement).closest("[data-ofrecer-a],[data-elegir],[data-reofrecer],[data-asignar]") as HTMLElement | null;
    if (!b) return;
    ev.preventDefault();
    if (ocupado()) return;
    const id = Number(b.dataset.ofrecerA ?? b.dataset.elegir ?? b.dataset.reofrecer ?? b.dataset.asignar);
    const turbo = datos.turbos.find((t) => t.delivery_id === id); if (!turbo) return;
    if (b.dataset.ofrecerA || b.dataset.asignar) {
      const sel = el.querySelector(`#tv-sel-${id}`) as HTMLSelectElement | null;
      const driverId = Number(sel?.value); if (!driverId) return;
      if (b.dataset.ofrecerA) acciones.ofrecerA(turbo, driverId); else acciones.asignar(turbo, driverId);
    } else if (b.dataset.elegir) acciones.elegir(turbo);
    else acciones.reofrecer(turbo);
  };
  // Al elegir otro chofer en el selector, el botón lo nombra.
  const onChange = (ev: Event) => {
    const sel = ev.target;
    if (sel instanceof HTMLSelectElement && sel.id.startsWith("tv-sel-")) rotularOfrecer(el, sel);
  };
  el.addEventListener("click", onClick);
  el.addEventListener("change", onChange);
  return () => { el.removeEventListener("click", onClick); el.removeEventListener("change", onChange); };
}

export type PopupLike = {
  getElement(): HTMLElement | undefined;
  update(): void;
  options: { autoPan?: boolean };
};

// El botón «Ofrecer» dice a quién se lo ofrece: el chofer elegido en el selector.
export function rotularOfrecer(root: ParentNode, sel: HTMLSelectElement) {
  const id = sel.id.replace(/^tv-sel-/, "");
  const boton = root.querySelector(`[data-ofrecer-a="${id}"]`);
  const nombre = sel.selectedOptions[0]?.dataset.nombre;
  if (boton) boton.textContent = nombre ? `Ofrecer a ${nombre}` : "Ofrecer a este chofer";
}

// Mientras el coordinador lo está usando (foco en el selector o en un botón)
// no se toca. Si no, se rearma conservando lo abierto (el Turbo, «Más
// acciones», el detalle y el scroll) y el chofer elegido de cada Turbo
// (aunque cambie el orden de la lista), sin recentrar el mapa: el autoPan es
// para cuando se abre, no para cada sondeo. Devuelve si lo rearmó.
export function refrescarPopup(popup: PopupLike | null | undefined, activo: Element | null = document.activeElement): boolean {
  const el = popup?.getElement();
  if (!popup || !el) return false;
  if (activo && el.contains(activo)) return false;
  const elegidos = new Map<string, string>();
  el.querySelectorAll("select").forEach((s) => elegidos.set(s.id, s.value));
  const abierto = leerEstado(el);
  const autoPan = popup.options.autoPan;
  popup.options.autoPan = false;
  try { popup.update(); } finally { popup.options.autoPan = autoPan; }
  aplicarEstado(el, abierto);
  el.querySelectorAll("select").forEach((s) => {
    const v = elegidos.get(s.id);
    if (v && Array.from(s.options).some((o) => o.value === v)) s.value = v;
    rotularOfrecer(el, s);
  });
  return true;
}
